import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { toast } from 'sonner';
import { FileText, Pencil, Trash2 } from 'lucide-react';
import type { Rental, RentalFilters, RentalCreateInput, RentalUpdateInput } from 'shared/schemas';
import { Button } from '../../../components/ui/button.tsx';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog.tsx';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet.tsx';
import { FilterBar } from '../../../components/layout/FilterBar.tsx';
import { KpiCard } from '../../../features/dashboard/components/KpiCard.tsx';
import { KpiStrip } from '../../../features/dashboard/components/KpiStrip.tsx';
import { EmptyStateV3 } from '../../../components/layout/EmptyStateV3.tsx';
import { PageHeader } from '../../../components/layout/PageHeader.tsx';
import { SectionHeader } from '../../../components/layout/SectionHeader.tsx';
import { RentalCard } from '../../../features/rentals/components/RentalCard.tsx';
import { RentalDeleteDialog } from '../../../features/rentals/components/RentalDeleteDialog.tsx';
import { RentalDetailCard } from '../../../features/rentals/components/RentalDetailCard.tsx';
import { RentalForm } from '../../../features/rentals/components/RentalForm.tsx';
import { RentalInvoicesPanel } from '../../../features/rentals/components/RentalInvoicesPanel.tsx';
import { RentalLifecycleActions } from '../../../features/rentals/components/RentalLifecycleActions.tsx';
import { RentalStatusBadge } from '../../../features/rentals/components/RentalStatusBadge.tsx';
import { useRental } from '../../../features/rentals/hooks/useRental.ts';
import { useRentals } from '../../../features/rentals/hooks/useRentals.ts';
import { useRentalsStats } from '../../../features/rentals/hooks/useRentalsStats.ts';
import { useCreateRental } from '../../../features/rentals/hooks/useCreateRental.ts';
import { useUpdateRental } from '../../../features/rentals/hooks/useUpdateRental.ts';
import { STRINGS } from '../../../features/rentals/rentals.strings.ts';

const rentalsSearchSchema = z.object({
  view: z.string().optional(),
  edit: z.string().optional(),
  new: z.boolean().optional(),
});

export const Route = createFileRoute('/_authed/rentals/')({
  validateSearch: (s) => rentalsSearchSchema.parse(s),
  component: RentalsPage,
});

function RentalSheetDetail({
  rentalId,
  onEdit,
  onDelete,
}: {
  rentalId: string;
  onEdit: () => void;
  onDelete: (rental: Rental) => void;
}) {
  const { data, isLoading } = useRental(rentalId);
  const rental = data?.data;

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!rental) {
    return (
      <div className="p-6 text-sm text-muted-foreground">No se encontró el alquiler.</div>
    );
  }

  return (
    <div className="overflow-y-auto p-6 flex flex-col gap-6">
      <RentalDetailCard
        rental={rental}
        onEdit={onEdit}
        onDelete={() => onDelete(rental)}
      />
      <RentalInvoicesPanel rental={rental} />
      <RentalLifecycleActions rental={rental} />
    </div>
  );
}

function RentalsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('todos');
  const [rentalToDelete, setRentalToDelete] = useState<Rental | null>(null);

  const { view: selectedId, new: isNewOpen, edit: editId } = Route.useSearch();

  const statusFilter =
    activeTab === 'activos' ? 'active'
    : activeTab === 'vencidos' ? 'ended'
    : activeTab === 'cancelados' ? 'cancelled'
    : undefined;

  const { data, isLoading } = useRentals({
    page: 1,
    search: search || undefined,
    status: statusFilter as RentalFilters['status'],
  });

  const stats = useRentalsStats();
  const createRental = useCreateRental();
  const updateRental = useUpdateRental(editId ?? '');

  const rentals = data?.data ?? [];
  const editRental = editId ? rentals.find((r) => r.id === editId) : undefined;

  const tabs = [
    { id: 'todos', label: 'Todos', count: stats.total },
    { id: 'activos', label: STRINGS.STATUS_ACTIVE, count: stats.activos },
    { id: 'vencidos', label: 'Vencidos', count: stats.vencidos },
    { id: 'cancelados', label: STRINGS.STATUS_CANCELLED, count: undefined },
  ];

  function openNew() {
    void navigate({ to: '/rentals', search: { new: true }, replace: true });
  }

  function closeModals() {
    void navigate({ to: '/rentals', search: {}, replace: true });
  }

  function handleCreate(formData: RentalCreateInput) {
    createRental.mutate(formData, {
      onSuccess: () => {
        toast.success(STRINGS.TOAST_CREATE_SUCCESS);
        closeModals();
      },
      onError: () => {
        toast.error(STRINGS.TOAST_ERROR_GENERIC);
      },
    });
  }

  function handleUpdate(formData: RentalUpdateInput) {
    updateRental.mutate(formData, {
      onSuccess: () => {
        toast.success(STRINGS.TOAST_UPDATE_SUCCESS);
        closeModals();
      },
      onError: () => {
        toast.error(STRINGS.TOAST_ERROR_GENERIC);
      },
    });
  }

  const selectedRental = rentals.find((r) => r.id === selectedId) ?? null;

  return (
    <div className="flex-1 overflow-auto px-3 sm:px-6 py-4 sm:py-6">
      <PageHeader
        title={STRINGS.LIST_TITLE}
        actions={
          <Button type="button" onClick={openNew}>
            {STRINGS.NEW_BUTTON}
          </Button>
        }
      />

      <KpiStrip>
        <KpiCard label="Total" value={String(stats.total)} />
        <KpiCard label={STRINGS.STATUS_ACTIVE} value={String(stats.activos)} />
        <KpiCard label="Vencidos" value={String(stats.vencidos)} />
        <KpiCard label="Este mes" value={String(stats.esteMes)} />
      </KpiStrip>

      <div className="mt-6">
        <SectionHeader title="Lista de alquileres" />

        <FilterBar
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={(id) => {
            setActiveTab(id);
          }}
          searchValue={search}
          onSearchChange={(value) => {
            setSearch(value);
          }}
          searchPlaceholder={STRINGS.PLACEHOLDER_SEARCH}
        />

        {isLoading ? (
          <div className="mt-4 text-sm text-muted-foreground">Cargando...</div>
        ) : rentals.length === 0 ? (
          <div className="mt-8">
            <EmptyStateV3
              icon={FileText}
              title="Sin alquileres"
              description={search ? STRINGS.NO_RESULTS : STRINGS.EMPTY_STATE}
              action={
                <Button type="button" onClick={openNew}>
                  {STRINGS.NEW_BUTTON}
                </Button>
              }
            />
          </div>
        ) : (
          <div
            data-testid="rentals-grid"
            className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2"
          >
            {rentals.map((rental) => (
              <RentalCard
                key={rental.id}
                rental={rental}
                onClick={() =>
                  void navigate({ to: '/rentals', search: { view: rental.id }, replace: true })
                }
              />
            ))}
          </div>
        )}
      </div>

      {/* Rental detail sheet */}
      <Sheet
        open={Boolean(selectedId)}
        onOpenChange={(open) => { if (!open) closeModals(); }}
      >
        <SheetContent side="right" className="w-full max-w-lg overflow-hidden p-0">
          {selectedId && (
            <>
              <SheetHeader className="border-b border-border p-6 pb-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    {selectedRental && (
                      <RentalStatusBadge status={selectedRental.status} />
                    )}
                    <SheetTitle className="truncate text-base">
                      {selectedRental?.clientName ?? STRINGS.DETAIL_TITLE}
                    </SheetTitle>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        void navigate({ to: '/rentals', search: { edit: selectedId }, replace: true })
                      }
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        if (selectedRental) setRentalToDelete(selectedRental);
                      }}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </div>
              </SheetHeader>

              <RentalSheetDetail
                rentalId={selectedId}
                onEdit={() =>
                  void navigate({ to: '/rentals', search: { edit: selectedId }, replace: true })
                }
                onDelete={(rental) => setRentalToDelete(rental)}
              />
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* New rental dialog */}
      <Dialog
        open={isNewOpen === true}
        onOpenChange={(open) => { if (!open) closeModals(); }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{STRINGS.NEW_BUTTON}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <RentalForm
              mode="create"
              formId="rental-create-form"
              onSubmit={handleCreate}
              isPending={createRental.isPending}
            />
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeModals}>
              {STRINGS.ACTION_CANCEL}
            </Button>
            <Button type="submit" form="rental-create-form" disabled={createRental.isPending}>
              {STRINGS.ACTION_SAVE}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit rental dialog */}
      <Dialog
        open={editRental !== null && editRental !== undefined}
        onOpenChange={(open) => { if (!open) closeModals(); }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{STRINGS.EDIT_TITLE}</DialogTitle>
          </DialogHeader>
          {editRental && (
            <>
              <DialogBody>
                <RentalForm
                  mode="edit"
                  formId="rental-edit-form"
                  initialValues={editRental}
                  onSubmit={handleUpdate}
                  isPending={updateRental.isPending}
                />
              </DialogBody>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeModals}>
                  {STRINGS.ACTION_CANCEL}
                </Button>
                <Button type="submit" form="rental-edit-form" disabled={updateRental.isPending}>
                  {STRINGS.ACTION_SAVE}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <RentalDeleteDialog
        rental={rentalToDelete}
        open={rentalToDelete !== null}
        onClose={() => {
          setRentalToDelete(null);
          closeModals();
        }}
      />
    </div>
  );
}
